import styled from '@emotion/styled';
import type { PropsWithChildren } from 'react';

import type { MarginProperties, PaddingProperties } from '@jane/shared/reefer';
import { Box } from '@jane/shared/reefer';
import { mediaQueries } from '@jane/shared/reefer-emotion';

interface SpecialDetailSummaryProps
  extends PropsWithChildren,
    MarginProperties,
    PaddingProperties {}

const SpecialDetailSummary = styled(Box)({
  width: '100%',
  height: 'min-content',
  position: 'relative',
  [mediaQueries.desktop('sm', 'min')]: {
    position: 'sticky',
    top: 80,
    maxWidth: '468px',
  },
});

export const SpecialDetailContent = styled(Box)({
  width: '100%',
});

const BaseLayout = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '24px',
  [mediaQueries.desktop('sm', 'min')]: {
    flexDirection: 'row',
    gap: '48px',
  },
});

const SpecialDetailBanner = styled(Box)({
  display: 'none',
  width: '100%',
  position: 'sticky',
  top: 0,
  zIndex: 'var(--layers-header)',
  '#special-detail-layout.summary-not-at-top &': {
    display: 'block',
  },
  [mediaQueries.desktop('sm', 'min')]: {
    display: 'none',
  },
});

const SpecialDetailSummaryWithAnchor = ({
  children,
  ...spacing
}: SpecialDetailSummaryProps) => {
  return (
    <SpecialDetailSummary {...spacing}>
      {children}
      <Box
        id="bottom-of-summary-anchor"
        height="1px"
        width="1px"
        position="absolute"
        bottom={-48}
      />
    </SpecialDetailSummary>
  );
};

const BaseLayoutWithObserver = ({
  children,
  ...spacing
}: SpecialDetailSummaryProps) => {
  if (
    'IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype
  ) {
    const observer = new IntersectionObserver((entries) => {
      const wrapperElement = document.querySelector('#special-detail-layout');

      if (!wrapperElement) {
        return;
      }

      if (entries[0].boundingClientRect.y < -1) {
        wrapperElement.classList.add('summary-not-at-top');
      } else {
        wrapperElement.classList.remove('summary-not-at-top');
      }
    });
    const targetElement = document.querySelector('#bottom-of-summary-anchor');
    if (targetElement) {
      observer.observe(targetElement);
    }
  }

  return (
    <BaseLayout {...spacing} id="special-detail-layout">
      {children}
    </BaseLayout>
  );
};

export const SpecialDetailLayout = Object.assign(BaseLayoutWithObserver, {
  Banner: SpecialDetailBanner,
  Content: SpecialDetailContent,
  Summary: SpecialDetailSummaryWithAnchor,
});
